import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from "@angular/core";
import { Router, RouterModule } from '@angular/router';
import { ConfirmationService, MessageService } from "primeng/api";
import { Subscription } from "rxjs";
import { USER_MGMT_CNST } from "../../constants/proj.cnst";
import { UM_TABLE_CNST } from "../../constants/table.cnst";
import { isNullOrUndefined } from "../../modules/shared";
import { AppStateService } from "../../services/app-state.service";
import { CommonService } from "../../services/common.service";
import { DataService } from "../../services/data.service";
import { EnvService } from "../../services/env.service";
import { HttpService } from "../../services/http.service";
import { UtilsService } from "../../services/utils.service";
import { SharedModule } from '../../shared/shared.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SkeletonModule } from '../../modules/shared/components/skeleton/skeleton.module';
import { PrimengBreadcrumbComponent } from '../../modules/shared/components/primeng-breadcrumb/primeng-breadcrumb.component';
import { PrimengTableComponent } from '../../modules/shared/components/primeng-table/primeng-table.component';
import { TableModule } from 'primeng/table';
import { ImportsModule } from '../../modules/imports/imports';
import { AuthorizationModule } from '../../modules/authorization';
import { ButtonModule } from 'primeng/button';
import * as TablerIcons from 'angular-tabler-icons/icons';
import { TablerIconComponent, provideTablerIcons } from 'angular-tabler-icons';

@Component({
  selector: "app-profiles",
  templateUrl: "./profiles.component.html",
  styleUrls: ["./profiles.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    AuthorizationModule,
    SkeletonModule,
    ImportsModule,
    ButtonModule,
    TableModule,
    CommonModule,
    PrimengTableComponent,
    PrimengBreadcrumbComponent,
    RouterModule,
    TablerIconComponent,
  ],
  providers:[
    provideTablerIcons({
      ...TablerIcons
    }),
  ]
})
export class ProfilesComponent implements OnInit {
  tableConfig: any = {};
  tableData: any = [];
  subscriptions: Subscription[] = [];
  isEditPermission!: boolean;
  isCustomer: any;
  customerListData: any = [];

  loadMoreRecordsOnScroll: boolean = true;
  currentPage: number = 0;
  currentOffset: number = 0;

  //*Infinite scrolling related variables
  scrollDistance!: number;
  throttle!: number;
  isScrollOnWindow!: boolean;

  wasPageScrolled!: boolean;
  loggedInUserFlag: boolean = false;
  currentResponseLength!: number;
  customerSelected: any;

  loading: boolean = false;
  currentOperation: any = null;
  isCustomerListFetched: boolean = false;

  totalRecordCount: number = 0;
  isCustomSizePresent: boolean = false;
  tableName: any = null;

  applicationList: any = [];
  modalData: any = [];
  dataTypes = UM_TABLE_CNST.CLICKABLE_DATA_TYPE;

  constructor(
    private router: Router,
    private dataSrv: DataService,
    private commonSrv: CommonService,
    private httpSrv: HttpService,
    private appStateSrv: AppStateService,
    private utilsSrv: UtilsService,
    private envService: EnvService,
    private datePipe: DatePipe,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
  ) { }

  ngOnInit() {
    let permissions = this.appStateSrv.getUserPermissions();

    if (permissions?.['User Management']?.['resources']?.['ROLE']?.['actions']) {
      this.isEditPermission = permissions['User Management']['resources']['ROLE']['actions'].indexOf('EDIT') !== -1;
    }

    let metaInfo : any =  localStorage.getItem("metaInfo")
    metaInfo = JSON.parse(metaInfo);
    this.isCustomer = !!(metaInfo && (metaInfo['customerId'] || metaInfo['customerName']));

    const operationRef = this.appStateSrv.operationRef.subscribe(
      (value: any) => {
        let { operation, postData } = value;

        this.currentOperation = operation;

        operation === USER_MGMT_CNST.OPERATION_TYPES.PROFILE.DELETE_PROFILE
          ? this.deleteProfile(postData)
          : this.updateProfile(postData);
      }
    );

    this.wasPageScrolled = this.appStateSrv.getWasPageScrolled();

    const pageScrolledRef = this.appStateSrv.wasPageScrolledRef.subscribe((value: boolean) => {
      this.wasPageScrolled = value;

      //* If page was scrolled update current page number and offset values
      if (this.wasPageScrolled) {
        this.commonTableDataFormater();
      }

      //* If current operation is not null and offset is not zero then
      //* add/edit/delete was performed on any one of the loaded records
      //* hence reset offset to 0 to load all records upto that point
      if (this.loadMoreRecordsOnScroll || !isNullOrUndefined(this.currentOperation)) {
        if (this.customerSelected) {
          this.customerBasedRoleList(this.customerSelected['customerId']);
        } else {

          let size: any = null;

          if (this.currentOffset !== 0 && !isNullOrUndefined(this.currentOperation)) {
            size = this.totalRecordCount + 1;
            this.isCustomSizePresent = true;
            this.currentOffset = 0;
          }

          this.setTableData(size);
        }
      }
    });

    this.subscriptions.push(operationRef);
    this.subscriptions.push(pageScrolledRef);

    this.setTableConfig();
    this.setTableData();
  }

  setTableConfig() {
    this.tableConfig = this.commonSrv.setTableConfig(!this.isCustomer ?
      USER_MGMT_CNST.LIST_NAME_MAPPING.PROFILE_LIST : USER_MGMT_CNST.LIST_NAME_MAPPING.PROFILE_CUSTOMER_LIST
    );
    this.tableConfig.hasInfiniteScroll = true;

    this.tableName = !this.isCustomer
      ? USER_MGMT_CNST.LIST_NAME_MAPPING.PROFILE_LIST
      : USER_MGMT_CNST.LIST_NAME_MAPPING.PROFILE_CUSTOMER_LIST;
  }

  /**
   * Note :
   * If current offset is 0 it means the edit/add/delete was performed
   * on any one of the existing records hence no need to load the
   * next set of records
   *
   * If offset is not 0 and add/edit/delete was performed, since
   * we don't know on which record it was performed we need to load all
   * the records from offset 0 to current offset.
  */
  async setTableData(size: any = null) {
    this.loading = true;

    let options = {
      'queryParams': {
        size: isNullOrUndefined(size) ? USER_MGMT_CNST.INFINITE_SCROLL_OPTIONS.INFINITE_SCROLL_LIMIT : size,
        offset: this.currentOffset
      }
    }

    let profilesListApiResponse = await this.commonSrv.getProfilesList(options);
    let responseKeys = Object.keys(profilesListApiResponse);

    this.loading = false;

    if (!this.isCustomerListFetched) {
      this.setCustomerListData();
    }

    if (responseKeys.length > 0) {
      let tableData = this.formatTableData(profilesListApiResponse);

      // * If page was scrolled and edit/add/delete was not performed
      if (this.wasPageScrolled && isNullOrUndefined(this.currentOperation)) {
        this.tableData = [...this.tableData, ...tableData];
      } else if (this.currentOffset === 0) {
        this.tableData = tableData;
      }

      this.totalRecordCount = this.currentOffset === 0
        ? tableData.length
        : this.totalRecordCount + tableData.length;

      this.currentOperation = null;

      if (!isNullOrUndefined(size)) {
        this.currentOffset = this.totalRecordCount + 1;
      }
    }
  }

  openAddProfileModal() {
    this.appStateSrv.setProfileData({ operation: 'new', info: null });
    this.router.navigate(['usermgmt', 'roles', 'config'], {
      queryParams: {
        operation: 'new'
      }
    });
  }

  addProfile(profileInfo: any, customerDetails: any) {
    let { name, description, application } = profileInfo;

    let profileData = this.appStateSrv.getProfileData();

    let postData = {
      "meta": {
        roleName: name,
        description,
        applicationId: application,
        customerId: customerDetails['customerId']
      }
      // roleDetailsJson: ""
    };

    this.httpSrv.makePostApiCall("ADD_PROFILE", postData, this.envService.baseUrl).subscribe({
      next: (response: any) => {
        if (response.statusCode === 201) {
          this.appStateSrv.setWasPageScrolled(false);
          this.currentOperation = profileData.operation;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: response.message });
        }
      },
      complete: () => { },
      error: (error) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: "Error occured while creating role" });
      }
    });
  }

  updateProfile(profileInfo: any) {
    let { postData, options } = this.setPostAndOptions(profileInfo);

    if (postData && options) {
      this.httpSrv.makePutApiCall("UPDATE_PROFILE", postData, this.envService.baseUrl, options).subscribe({
        next: (response: any) => {
          this.appStateSrv.setWasPageScrolled(false);
          this.messageService.add({ severity: 'success', summary: 'Success', detail: response.message });
        },
        complete: () => { },
        error: (err) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: "Error occured while updating role" });
        }
      });
    } else {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: "No field is edited for updating" });
    }
  }

  deleteProfile(profileInfo: any) {
    let options = {
      uriParams: {
        roleId: profileInfo.roleId
      }
    };
    this.httpSrv.makeDeleteApiCall("DELETE_PROFILES", this.envService.baseUrl, options).subscribe({
      next: (response: any) => {
        this.appStateSrv.setWasPageScrolled(false);

        this.tableData = this.tableData.filter(
          (value: any) => value.roleId != profileInfo.role_id
        );
        this.messageService.add({ severity: 'success', summary: 'Success', detail: response.message });
      },
      complete: () => { },
      error: (error) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message });
      }
    });
  }

  setPostAndOptions(profileInfo: any) {
    if (!profileInfo) {
      return { postData: null, options: null };
    }

    const { addResourceActions, deleteResourceActions, meta, roleDetailsJson } = profileInfo;

    if (!addResourceActions.length && !deleteResourceActions.length && !meta && !roleDetailsJson) {
      return { postData: null, options: null };
    }

    let postData: any = {
      addResourceActions,
      deleteResourceActions
    }

    if (profileInfo.meta) {
      postData.meta = profileInfo.meta;
    }

    if (roleDetailsJson) {
      postData.roleDetailsJson = roleDetailsJson
    }

    return {
      postData,
      options: {
        replaceParams: {
          "REPLACE_ROLE_ID": profileInfo.role_id
        }
      }
    }
  }

  setCustomerListData() {

    let userMetaInfo : any = localStorage.getItem("metaInfo")
    userMetaInfo = JSON.parse(userMetaInfo);

    if (Object.keys(userMetaInfo).length > 0) {

      let { customerId } = userMetaInfo;

      // Fetch customer list only for super admin login
      if (isNullOrUndefined(customerId)) {
        this.httpSrv.makeGetApiCall('GET_CUSTOMERS_LIST', this.envService.baseUrl).subscribe({
          next: (response: any) => {
            let customerListData = this.transformData(response);
            this.customerListData = typeof this.dataSrv.addOptionsToTableData(customerListData, UM_TABLE_CNST.RESOURCES_LIST.customers, this.tableName) ? this.dataSrv.addOptionsToTableData(customerListData, UM_TABLE_CNST.RESOURCES_LIST.customers, this.tableName) : [];
            this.isCustomerListFetched = true;
          },
          complete: () => { },
          error: error => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: "Failed to fetch customers list" });
          }
        })
      }
    }
  }

  transformData(response: any) {
    if (!Array.isArray(response)) {
      return Object.values(response);
    } else {
      return response;
    }
  }

  onCustomerSelect(e: any) {
    if (!isNullOrUndefined(e)) {
      this.resetVariables();
      this.customerBasedRoleList(e.customerId)
    }
  }

  clearCustomerOptions() {
    this.resetVariables();
    this.setTableData();
  }

  resetVariables() {
    this.loadMoreRecordsOnScroll = true;
    this.currentPage = 0;
    this.currentOffset = 0;
    this.loggedInUserFlag = false;
    this.tableData = [];
  }

  async customerBasedRoleList(customerId: any) {

    let options = {
      'uriParams': {
        customerId: customerId
      },
      'queryParams': {
        size: USER_MGMT_CNST.INFINITE_SCROLL_OPTIONS.INFINITE_SCROLL_LIMIT,
        offset: this.currentOffset
      }
    }

    this.loading = true;

    let profilesListApiResponse = await this.commonSrv.getProfilesList(options);
    let responseKeys = Object.keys(profilesListApiResponse);

    this.loading = false;

    if (responseKeys.length > 0) {
      let tableData = this.formatTableData(profilesListApiResponse);
      this.tableData = [...this.tableData, ...tableData];
      this.commonTableDataFormater();
    }
  }

  formatTableData(profilesListApiResponse: any): any[] {
    let list = profilesListApiResponse['response'];

    let updatedList = this.transformData(list)
    return updatedList;
    // let tableData: any = this.dataSrv.addOptionsToTableData(
    //   updatedList,
    //   UM_TABLE_CNST.RESOURCES_LIST.profile,
    //   this.tableName
    // );

    // console.log("tableData==", tableData)

    // if (tableData?.length === 0) {
    //   this.loadMoreRecordsOnScroll = false;
    // }

    // this.currentResponseLength = tableData.length;

    // return tableData;
  }

  commonTableDataFormater() {
    this.currentPage++;

    if (!this.isCustomSizePresent) {
      this.currentOffset = this.currentOffset +
        USER_MGMT_CNST.INFINITE_SCROLL_OPTIONS.INFINITE_SCROLL_LIMIT + 1;
    } else {
      this.isCustomSizePresent = false;
    }
  }

  systemRoles: string[] = USER_MGMT_CNST.SYSTEM_ROLES;

  handleOptionClick(rowData: any, operation: string) {
    if (operation == USER_MGMT_CNST.OPTIONS.VIEW) {
      let postData = rowData;

      if (this.utilsSrv.isObjectNotNullAndEmpty(postData)) {
        let { roleId } = postData;

        this.appStateSrv.setProfileData({ operation: operation.toLowerCase(), info: postData });

        this.router.navigate(['usermgmt', 'roles', 'config'], {
          queryParams: {
            operation: operation.toLowerCase(),
            config: 'info',
            roleId
          }
        });
      }
    } else if (operation == USER_MGMT_CNST.OPTIONS.DELETE) {
      this.confirmationService.confirm({
        message: 'Do you want to delete this role?',
        header: 'Confirmation',
       

        // rejectButtonStyleClass: "p-button-text",
        rejectButtonProps: {
          label: 'Cancel',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Delete',
          severity: 'danger',
          // size:'large'
        },

        accept: () => {
          this.deleteProfile(rowData);
        },
        reject: () => {

        }
      });
    } else {
      this.appStateSrv.setProfileData({ operation: 'new', info: null });
      this.httpSrv.makeGetApiCall('APP_LIST', this.envService.baseUrl).subscribe({
        next: (response: any) => {
          this.applicationList = response;
          const roleId = rowData['roleId'];
          const applicationId = rowData['applicationId'];
          this.router.navigate(['usermgmt', 'roles', 'config'], {
            queryParams: {
              operation: 'edit',
              config: 'info',
              roleId,
              applicationId
            }
          });
        },
        complete: () => { },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: "Error occured while fetching list of applications" });
        }
      })

    }
  }

  setOperationType(value: any) {
    this.appStateSrv.operationRef.next(value);
  }


  ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }
}
